import React, { forwardRef, useRef } from 'react'
import { Collapsible, Inline } from '@airbus/components-react/components'
import { useDrag, useDrop } from 'react-dnd'
import LockIcon from '@mui/icons-material/Lock'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'

type Props = {
  hoverIndex: number
  setHoverIndex: (n: number | null) => void
  dropPosition: 'top' | 'bottom'
  setDropPosition: (p: 'top' | 'bottom' | null) => void
  index: number
  moveListItem: (a: number, b: number) => void
  children: React.ReactNode
  isLocked: boolean
  style: React.CSSProperties
  title: string
  onExpandedChange: (expanded: boolean) => void
  expanded: boolean
}

type CollapsibleHeaderProps = React.HTMLAttributes<HTMLDivElement> & { isLocked: boolean; title: string }

const CollapsibleHeader = forwardRef<HTMLDivElement, CollapsibleHeaderProps>(function CollapsibleHeader(props, ref) {
  return (
    <div ref={ref} style={{ cursor: !props.isLocked ? 'grab' : 'pointer', width: '100%' }} {...props}>
      <Inline spacing='2-x' alignY='center' wrapChildren={false}>
        {props.isLocked ? <LockIcon /> : <DragIndicatorIcon />}
        <span>{props.title}</span>
      </Inline>
    </div>
  )
})

const DraggableCollapsibleItem = ({
  hoverIndex,
  setHoverIndex,
  dropPosition,
  setDropPosition,
  index,
  moveListItem,
  children,
  isLocked,
  style,
  title,
  expanded,
  onExpandedChange,
}: Props) => {
  // useDrag - the list item is draggable
  const [{ isDragging }, dragRef] = useDrag({
    type: 'item',
    item: { index },
    end: () => {
      setHoverIndex(null)
      setDropPosition(null)
    },
    canDrag: !isLocked,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  // useDrop - the list item is also a drop area
  const [, dropRef] = useDrop({
    accept: 'item',
    canDrop: () => !isLocked,
    hover: (it, monitor) => {
      const currentDragIndex = it.index
      const currentHoverIndex = index

      if (currentDragIndex === currentHoverIndex) return

      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const hoverClientY = monitor.getClientOffset().y - hoverBoundingRect.top

      const position = hoverClientY < hoverMiddleY ? 'top' : 'bottom'
      setDropPosition(position)
      setHoverIndex(currentHoverIndex)
    },
    drop: (it) => {
      const fromIndex = it.index
      let toIndex = hoverIndex

      if (dropPosition === 'bottom') {
        toIndex += fromIndex < toIndex ? 0 : 1
      } else {
        toIndex += fromIndex < toIndex ? -1 : 0
      }

      if (fromIndex === toIndex) {
        setHoverIndex(null)
        setDropPosition(null)
        return
      }
      moveListItem(fromIndex, toIndex)
      setHoverIndex(null)
      setDropPosition(null)
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  })

  // attach dropRef to container ref
  const ref = useRef(null)
  dropRef(ref)

  // Make items being dragged transparent, so it's easier to see where we drop them
  const opacity = isDragging ? 0 : 1

  return (
    <div ref={ref} style={{ ...style, opacity }} onClick={(e) => e.stopPropagation()}>
      {!isLocked && hoverIndex === index && dropPosition === 'top' && (
        <Collapsible title={<p></p>} style={{ borderStyle: 'dashed' }} />
      )}
      {!isDragging && (
        <Collapsible
          expanded={expanded}
          onChange={(event, expanded) => onExpandedChange(expanded)}
          title={<CollapsibleHeader ref={dragRef} title={title} isLocked={isLocked} />}
        >
          {children}
        </Collapsible>
      )}
      {!isLocked && hoverIndex === index && dropPosition === 'bottom' && (
        <Collapsible title={<p></p>} style={{ borderStyle: 'dashed' }} />
      )}
    </div>
  )
}

export default DraggableCollapsibleItem
